import { Box, Button, Flex } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMinimalMRTOptions } from "../../../mantine/getMinimalMRTOptions";
import { BasicConfirmationModal } from "../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import { DeleteConfirmationModal } from "../../BuildingBlocks/Layout/Modals/DeleteConfirmationModal/DeleteConfirmationModal";
import { getFirstDateColumn } from "./columns/getFirstDateColumn";
import { getLastDateColumn } from "./columns/getLastDateColumn";
import { getValueColumn } from "./columns/getValueColumn";
import { getRowId, usePeriodBasedTable } from "./hooks/usePeriodBasedTable";
import { EMPTY_ERRORS } from "./PeriodBasedTable.constants";
import type {
  CreateRowFn,
  ExternalErrors,
  PeriodBasedRow,
  ValidateRowFn,
  ValidationErrors,
  ValueColumnConfig
} from "./PeriodBasedTable.types";

export interface PeriodBasedTableProps<T extends Record<string, unknown>> {
  periodBasedData: PeriodBasedRow<T>[];
  onChange: (updatedRows: PeriodBasedRow<T>[]) => void;
  valueColumn: ValueColumnConfig<T>;
  additionalColumns?: MRT_ColumnDef<PeriodBasedRow<T>>[];
  readOnly?: boolean;
  externalErrors?: ExternalErrors;
  validateRow?: ValidateRowFn<T>;
  createRow: CreateRowFn<T>;
  deleteModalDataTypeName?: string;
  DeleteModalBody?: (row: PeriodBasedRow<T>) => React.ReactNode;
  enableDeleteForRow?: (
    row: PeriodBasedRow<T>,
    index: number,
    allRows: PeriodBasedRow<T>[]
  ) => boolean;
  editModalDataTypeName?: string;
  EditModalBody?: (
    row: PeriodBasedRow<T>,
    newValues: Record<string, unknown>
  ) => React.ReactNode;
}

export function TableCellErrorText({ error }: { error?: string }) {
  if (!error) return null;
  return (
    <Box c="error" fz="12px" mt="4px" style={{ whiteSpace: "break-spaces" }}>
      {error}
    </Box>
  );
}

export function TableCellWarningText({ warning }: { warning?: string }) {
  if (!warning) return null;
  return (
    <Box
      c="var(--mantine-color-warning-filled)"
      fw="bold"
      fz="xs"
      mt="4px"
      style={{ whiteSpace: "break-spaces" }}
    >
      {warning}
    </Box>
  );
}

/** @deprecated Please use ChangeableFieldTable instead. */
function PeriodBasedTable<T extends Record<string, unknown>>({
  periodBasedData,
  onChange,
  valueColumn,
  additionalColumns = [],
  readOnly = false,
  externalErrors,
  validateRow: customValidateRow,
  createRow,
  deleteModalDataTypeName,
  DeleteModalBody,
  enableDeleteForRow,
  editModalDataTypeName,
  EditModalBody
}: PeriodBasedTableProps<T>) {
  const validateRowData = useCallback(
    (
      values: PeriodBasedRow<T>
    ): {
      isValid: boolean;
      errors: ValidationErrors;
      warnings?: ValidationErrors;
    } => {
      const errors: ValidationErrors = {};

      if (!values.first_date) {
        errors.firstDate = "Startdatum ist erforderlich";
      }

      const fieldValue = (values as Record<string, unknown>)[
        valueColumn.fieldName
      ];
      const isEmpty =
        fieldValue === undefined || fieldValue === null || fieldValue === "";

      if (isEmpty) {
        const displayName =
          valueColumn.validationLabel || valueColumn.header || "Feld";
        errors.value = `${displayName} ist erforderlich`;
      }

      return {
        isValid: !errors.firstDate && !errors.value,
        errors
      };
    },
    [valueColumn.fieldName, valueColumn.validationLabel, valueColumn.header]
  );

  const validateRow = useCallback(
    (values: PeriodBasedRow<T>) => {
      const baseValidation = validateRowData(values);

      // If custom validation provided, merge it with base validation
      if (customValidateRow) {
        const customValidation = customValidateRow(values);
        return {
          isValid: baseValidation.isValid && customValidation.isValid,
          errors: { ...baseValidation.errors, ...customValidation.errors }
        };
      }

      return baseValidation;
    },
    [validateRowData, customValidateRow]
  );

  const [creationErrors, setCreationErrors] =
    useState<ValidationErrors>(EMPTY_ERRORS);
  const [editingErrors, setEditingErrors] =
    useState<ValidationErrors>(EMPTY_ERRORS);
  const [tableRows, setTableRows] = useState<PeriodBasedRow<T>[]>([]);

  const notifyChange = useCallback(
    (rows: PeriodBasedRow<T>[]) => !readOnly && onChange(rows),
    [onChange, readOnly]
  );

  const {
    pendingDateChanges,
    setPendingDateChanges,
    rowToDelete,
    setRowToDelete,
    rowToEdit,
    setRowToEdit,
    handleUpdatePreviousRowLastDate,
    handleUpdateNextRowFirstDate,
    handleDeleteRow,
    handleConfirmDelete,
    handleConfirmEdit
  } = usePeriodBasedTable<T>({
    tableRows,
    setTableRows,
    notifyChange
  });

  useEffect(() => {
    setTableRows(periodBasedData || []);
  }, [periodBasedData]);

  const handleConfirmEditWithCleanup = useCallback(() => {
    handleConfirmEdit();
    setEditingErrors(EMPTY_ERRORS);
  }, [handleConfirmEdit]);

  const displayRows = useMemo(() => {
    return tableRows.map((row) => {
      const rowId = getRowId(row);
      const pendingChanges = pendingDateChanges[rowId];
      if (pendingChanges) {
        return {
          ...row,
          ...pendingChanges
        };
      }
      return row;
    });
  }, [tableRows, pendingDateChanges]);

  const columns = useMemo(() => {
    const baseColumns = [
      getFirstDateColumn({
        rows: displayRows as PeriodBasedRow[],
        originalRows: tableRows as PeriodBasedRow[],
        creationErrors,
        editingErrors,
        externalErrors,
        onUpdatePreviousRowLastDate: readOnly
          ? undefined
          : handleUpdatePreviousRowLastDate
      }) as MRT_ColumnDef<PeriodBasedRow<T>>,
      getLastDateColumn({
        rows: displayRows as PeriodBasedRow[],
        originalRows: tableRows as PeriodBasedRow[],
        externalErrors,
        onUpdateNextRowFirstDate: readOnly
          ? undefined
          : handleUpdateNextRowFirstDate
      }) as MRT_ColumnDef<PeriodBasedRow<T>>,
      getValueColumn({
        header: valueColumn.header,
        fieldName: valueColumn.fieldName,
        CellComponent: valueColumn.CellComponent,
        EditComponent: valueColumn.EditComponent,
        creationErrors,
        editingErrors,
        externalErrors
      }),
      ...additionalColumns
    ];

    return baseColumns;
  }, [
    displayRows,
    tableRows,
    valueColumn,
    additionalColumns,
    creationErrors,
    editingErrors,
    handleUpdatePreviousRowLastDate,
    handleUpdateNextRowFirstDate,
    readOnly,
    externalErrors
  ]);

  const table = useMantineReactTable<PeriodBasedRow<T>>({
    ...getMinimalMRTOptions<PeriodBasedRow<T>>({
      emptyRowsFallbackText: "Keine Einträge vorhanden"
    }),
    ...(readOnly
      ? {}
      : getDefaultMRTRowEditModeOptions<PeriodBasedRow<T>>({
          rowActions: {
            edit: { text: "Bearbeiten" },
            delete: {
              text: "Löschen",
              onClick: handleDeleteRow,
              enableDelete: enableDeleteForRow
                ? (cell) => {
                    const rowIndex = cell.row.index;
                    return enableDeleteForRow(
                      cell.row.original,
                      rowIndex,
                      displayRows
                    );
                  }
                : undefined
            }
          }
        })),
    columns,
    data: displayRows,
    getRowId,
    enableRowActions: !readOnly,
    positionCreatingRow: "bottom",
    enableBottomToolbar: !readOnly,
    onCreatingRowSave: ({ values, exitCreatingMode }) => {
      const rowToValidate = createRow(values);
      const { isValid, errors } = validateRow(rowToValidate);
      setCreationErrors(errors);

      if (isValid) {
        const rowsWithPendingChanges = tableRows.map((row) => {
          const rowId = getRowId(row);
          const pendingChanges = pendingDateChanges[rowId];
          if (pendingChanges) {
            return { ...row, ...pendingChanges };
          }
          return row;
        });

        const newRow: PeriodBasedRow<T> = { ...rowToValidate };
        const updatedRows = [...rowsWithPendingChanges, newRow];

        setTableRows(updatedRows);
        notifyChange(updatedRows);
        exitCreatingMode();
        setCreationErrors(EMPTY_ERRORS);
        setPendingDateChanges({});
      }
    },
    onEditingRowSave: ({ row, values, exitEditingMode }) => {
      const allValues = { ...values, ...row._valuesCache };

      const mergedValues: PeriodBasedRow<T> = { ...row.original, ...allValues };
      const { isValid, errors } = validateRow(mergedValues);
      setEditingErrors(errors);

      if (isValid) {
        const hasChanges = Object.keys(allValues).some((key) => {
          const originalValue = (row.original as Record<string, unknown>)[key];
          const newValue = (allValues as Record<string, unknown>)[key];
          return originalValue !== newValue;
        });

        const hasPendingDateChanges =
          Object.keys(pendingDateChanges).length > 0;

        if (!hasChanges && !hasPendingDateChanges) {
          exitEditingMode();
          setEditingErrors(EMPTY_ERRORS);
          setPendingDateChanges({});
          return;
        }

        const editModalBody = EditModalBody?.(mergedValues, allValues);
        const shouldShowEditConfirmationModal = editModalBody != null;

        if (shouldShowEditConfirmationModal) {
          setRowToEdit({ row, values: allValues, exitEditingMode });
        } else {
          const originalRowId = getRowId(row.original);
          const updatedRows = tableRows.map((tableRow) => {
            const currentRowId = getRowId(tableRow);
            if (currentRowId === originalRowId) {
              return mergedValues;
            }
            const pendingChanges = pendingDateChanges[currentRowId];
            if (pendingChanges) {
              return { ...tableRow, ...pendingChanges };
            }
            return tableRow;
          });

          setTableRows(updatedRows);
          notifyChange(updatedRows);
          exitEditingMode();
          setEditingErrors(EMPTY_ERRORS);
          setPendingDateChanges({});
        }
      }
    },
    onEditingRowCancel: () => {
      setEditingErrors(EMPTY_ERRORS);
      setPendingDateChanges({});
    },
    onCreatingRowCancel: () => {
      setCreationErrors(EMPTY_ERRORS);
      setPendingDateChanges({});
    },
    renderBottomToolbarCustomActions: () => {
      const isAddButtonDisabled =
        table.getState().creatingRow !== null ||
        table.getState().editingRow !== null;

      return (
        <Flex justify="center" style={{ width: "100%" }}>
          <Button
            disabled={isAddButtonDisabled}
            leftSection={<IconPlus size={18} />}
            variant="transparent"
            onClick={() => table.setCreatingRow(true)}
          >
            Hinzufügen
          </Button>
        </Flex>
      );
    }
  });

  return (
    <>
      <MantineReactTable table={table} />
      <DeleteConfirmationModal
        headerName={
          deleteModalDataTypeName
            ? `Eintrag für ${deleteModalDataTypeName}`
            : "Eintrag"
        }
        modalBodyElement={
          rowToDelete ? DeleteModalBody?.(rowToDelete.original) : undefined
        }
        name={deleteModalDataTypeName || "den Eintrag"}
        opened={rowToDelete !== null}
        toggleModal={() => {
          setRowToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
      <BasicConfirmationModal
        confirmationButtonColor="secondary"
        confirmationButtonText="Ja, speichern"
        opened={rowToEdit !== null}
        title={
          editModalDataTypeName
            ? `Eintrag für ${editModalDataTypeName} speichern`
            : "Eintrag speichern"
        }
        toggleModal={() => {
          setRowToEdit(null);
        }}
        onCancel={() => {
          setRowToEdit(null);
        }}
        onConfirm={handleConfirmEditWithCleanup}
      >
        {rowToEdit && EditModalBody
          ? EditModalBody(rowToEdit.row.original, rowToEdit.values)
          : undefined}
      </BasicConfirmationModal>
    </>
  );
}

export { PeriodBasedTable };
